import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { Product } from "./shop.product.model";
import { OptionService } from "../option/option.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { AmountComponent } from "../amount/amount.component";
import { ShopCartService } from './shop.cart.service';
import { ShopProductService } from './shop.product.service';
import { BundlesService } from '../service/bundles.service';
import { Bundle } from './shop.bundle.model';

@Component({
    selector: '[shop-list-item]',
    templateUrl: './shop.list.item.component.html',
    providers: []
})
export class ShopListItemComponent implements OnInit {

    /**
     * The current donation
     */
    @Input() currency: string;

    /**
     * current item
     */
    @Input() item: Product | Bundle;

    /**
     * Eventemitter when an item is put to the cart
     */
    @Output() addItem: EventEmitter<{ product: Product, count: number, increaseCountIfProductsExists: boolean, freePrice: number, text: string, forceCountModifiable?: boolean }> = new EventEmitter();

    /**
     * Eventemitter if a single product should be shown.
     */
    @Output() onShowItem: EventEmitter<Product | Bundle> = new EventEmitter();

    /**
     * Eventemitter if the download for a product shoud be shown
     */
    @Output() onShowDownloadForProduct: EventEmitter<Product> = new EventEmitter();

    /**
     * Eventemitter when the available count has changed
     */
    @Output() availableCountChanged: EventEmitter<void> = new EventEmitter();

    /**
     * Formgroup for validating the input in some cases.
     */
    form: FormGroup;

    /**
     * The number of items to put into the chart
     */
    itemCount: number = 1;

    /**
     * path to the product image
     */
    image: string;

    /**
     * toggle for showing an added animation
     */
    added: boolean = false;

    /**
     * has this item a free price selection
     */
    freePrice: boolean = false;

    /**
     * toggle for the textinput
     */
    textinput: boolean = false;

    /**
     * the maximum length of the textinput
     */
    textinputMaxLength: number = 250;

    /**
     * minimum value when using free price input
     */
    freePriceMin: number = 1;

    /**
     * the maximum price
     */
    priceMax: number = AmountComponent.SHOP_MAX;

    /**
     * toggle for the error message on a product
     */
    showInputError: boolean = false;

    /**
     * show count input field for free price input
     */
    showCountInput: boolean = false;

    /**
     * available count of the product
     */
    availableCount: number = -1;

    /**
     * toggle for showing the availability info
     */
    availabilityInfoShownProduct: boolean = false;
    availabilityInfoShownBundle: boolean = false;

    /**
     * how to format the price
     */
    priceFormat: string = '1.0-0';

    /**
     * flag if this item was changed by the user
     */
    changedByUser: boolean = false;

    /**
     * constructor
     * @param optionService
     * @param shopCartService
     * @param bundlesService
     * @param bundlesService
     * @param shopProductService
     */
    constructor(private optionService: OptionService, private shopCartService: ShopCartService,
                private bundlesService: BundlesService, private shopProductService: ShopProductService) {}

    /**
     * @inheritDoc
     */
    ngOnInit(): void {
        this.form = new FormGroup({});

        if (this.optionService.getOption('shop_textlength_max')) {
            this.textinputMaxLength = parseInt(this.optionService.getOption('shop_textlength_max'));
        }

        if (this.shopProductService.getDecimalsShownList()) {
            this.priceFormat = '1.2-2';
        }

        if (this.item.images && this.item.images.length > 0) {
            this.image = this.optionService.getImageDirectory() + '/' + (this.item.images.filter(image => image.forgift === false || image.forgift === null)[0]?.file ?? '');
        }

        // check if we have a free price input
        this.freePrice = this.item.price === null || this.item.price === "";

        // check if we need a textinput
        this.textinput = (this.item instanceof Product) && this.item.has_textinput === true;
        if (this.textinput === true) {
            this.form.addControl('textinput', new FormControl(null, [Validators.required, Validators.maxLength(this.textinputMaxLength), Validators.minLength(1)]));
        }

        // set the freeprice min
        if ((this.item instanceof Product) && this.item.has_zero_price === true) {
            this.freePriceMin = 0;
        }

        if (this.freePrice === true) {
            this.form.addControl('price', new FormControl(null, [Validators.required, Validators.min(this.freePriceMin), Validators.max(AmountComponent.SHOP_MAX)]));
        }

        // if we have a free price input or free text input, check if we need to show the count input
        if (this.freePrice || this.textinput) {
            this.showCountInput = (this.item instanceof Product) && this.item.has_force_count === true;
        }

        if ((this.item instanceof Bundle)) {
            this.bundlesService.prepareBundleInformation(this.item);
        }

        this.setProductAvailableCount();

        this.shopCartService.itemsUpdated.subscribe(() => {
            this.setProductAvailableCount();
        });
    }

    /**
     * add the current product and the choosen count to the cart
     */
    addItemToChart(): void {
        this.showInputError = false;

        // set the changed by user flag
        this.changedByUser = true;

        // handle bundles
        if ((this.item instanceof Bundle) && this.item.type === 'bundle') {
            if (this.item.isPurchasable) {
                this.bundlesService.addBundleToCart(this.item.bundle_products, this.itemCount);
            }
        }

        // handle products
        if ((this.item instanceof Product) && this.item.type === 'product') {
            if (this.freePrice && this.textinput) {
                const priceControl = this.form.controls['price'];
                const priceIsInvalid = priceControl?.value < this.freePriceMin || priceControl?.invalid === true;
                const textInput = this.form.controls['textinput'];
                const textIsInvalid = textInput?.invalid === true;

                if (priceIsInvalid || textIsInvalid) {
                    this.showInputError = true;
                    return;
                }

                this.addItem.emit({ product: this.item, count: this.itemCount, increaseCountIfProductsExists: this.showCountInput, freePrice: priceControl?.value, text: textInput.value, forceCountModifiable: this.showCountInput });
                textInput.setValue('');
            }
            else if (this.freePrice === true) {
                const priceControl = this.form.controls['price'];
                const priceIsInvalid = priceControl?.value < this.freePriceMin || priceControl?.invalid === true;

                if (priceIsInvalid) {
                    // do not add the product without value
                    this.showInputError = true;
                    return;
                }

                this.addItem.emit({ product: this.item, count: this.itemCount, increaseCountIfProductsExists: this.showCountInput, freePrice: priceControl?.value, text: null, forceCountModifiable: this.showCountInput });
            }
            else if (this.textinput === true) {
                const textInput = this.form.controls['textinput'];
                const textIsInvalid = textInput?.invalid === true;

                if (textIsInvalid) {
                    this.showInputError = true;
                    return;
                }

                const count = this.showCountInput ? this.itemCount : 1;

                this.addItem.emit({ product: this.item, count, increaseCountIfProductsExists: this.showCountInput, freePrice: null, text: textInput.value, forceCountModifiable: this.showCountInput });
                textInput.setValue('');
            }
            else {
                this.itemCount = Math.max(1, Math.floor(this.itemCount));
                this.addItem.emit({ product: this.item, count: this.itemCount, increaseCountIfProductsExists: true, freePrice: null, text: null });
            }
        }
        this.itemCount = 1;
        this.added = true;
        setTimeout(() => this.added = false, 2000);
    }

    /**
     * Product is clicked.
     */
    showItem(): void {
        this.onShowItem.emit(this.item);
    }


    /**
     * Current product download is clicked.
     */
    showDownload(): void {
        if (this.item instanceof Product && this.item.type === 'product') {
            this.onShowDownloadForProduct.emit(this.item);
        }
    }

    /**
     * check if the product is already in the cart and set the available count
     */
    setProductAvailableCount(): void {
        // get all products with the same id
        const productInCart = this.shopCartService.items.filter(element => element.product.id === this.item.id);
        // sum up the count of these products
        const productCount = productInCart.reduce((count, product) => count + product.count, 0);

        if (this.item instanceof Bundle && this.item.type === 'bundle') {
            this.availableCount = this.bundlesService.getAvailableCountForBundle(this.item);
        } else if (this.item instanceof Product && productCount > 0) {
            this.availableCount = this.item.getMaxCount() - productCount;
        } else if (this.item instanceof Product) {
            this.availableCount = this.item.getMaxCount();
        }

        // emit the available count
        this.availableCountChanged.emit();

        // check if the availability info should be shown
        if (this.changedByUser) {
            if (this.availableCount === 0) {
                if (this.item instanceof Product) {
                    this.availabilityInfoShownProduct = true;
                }
                if (this.item instanceof Bundle) {
                    this.availabilityInfoShownBundle = true;
                }
            }

        }
    }

    /**
     * change item count
     */
    changeItemCount(newCount: number): void {
        this.itemCount = newCount
        if (this.availableCount > 0 && this.itemCount > this.availableCount) {
            this.itemCount = this.availableCount;
        } else if (this.availableCount === 0) {
            this.itemCount = 1;
        } else {
            this.itemCount = this.itemCount;
        }

        this.changedByUser = true;
    }

    /**
     * cast to product
     */
    asProduct(item: Product | Bundle): Product {
        return item as Product;
    }
}
